import { setCssVariable } from '~/utils/setCssVariable'

export default function useTheme() {
  const { themeConfig } = useThemeConfig()

  // TODO: get rid of tenant here, move all to theme config on tenant
  const { tenant } = useTenant()
  // TODO: also refactor the color config out
  // Put these here so it applies across all layouts, including the empty one
  const colorConfig = useColorConfig()

  // Set css variables and nui props based on the theme config
  function applyThemeConfig(themeConfig: ThemeConfig) {
    if (!themeConfig) {
      console.error(`[useTheme] 🎨 No theme config found`)
      return
    }

    // Update the font families
    if (themeConfig.fonts?.sans) setCssVariable('font-sans', themeConfig.fonts.sans)
    if (themeConfig.fonts?.heading) setCssVariable('font-heading', themeConfig.fonts.heading)

    // Update the css variables
    Object.entries(themeConfig.cssVariables).forEach(([name, value]) => {
      if (name && value) {
        setCssVariable(name, value as string)
      }
    })
  }

  function updateColors(config: ReturnType<typeof useColorConfig>) {
    // Set the primary color by either using custom color shades or the preset
    if (config.primaryColorShades.value) applyCustomColorShades(config.primaryColorShades.value, 'primary')
    else if (config.primaryColorPreset.value) switchColorPreset('primary', config.primaryColorPreset.value)

    // Set the secondary color by either using custom color shades or the preset
    if (config.secondaryColorShades.value) applyCustomColorShades(config.secondaryColorShades.value, 'secondary')
    // Fallback to primary as the secondary color
    else if (config.secondaryColorPreset.value) switchColorPreset('secondary', config.secondaryColorPreset.value || config.primaryColorPreset.value)
    // Otherwise use a muted color
    else switchColorPreset('secondary', config.mutedColorPreset.value || 'slate')

    // Set the muted color
    switchColorPreset('muted', config.mutedColorPreset.value || 'slate')

    // Set the background color
    // Fallback to the muted-50 color
    setCssVariable('color-theme-background', config.backgroundColor.value || 'rgb(var(--color-muted-50))')
    setCssVariable('color-theme-background-dark', 'rgb(var(--color-muted-950))')

    // Base border color
    setCssVariable('color-theme-border', 'rgb(var(--color-secondary-100))')
    // Header border is the same as base by default
    setCssVariable('color-theme-header-border', 'rgb(var(--color-secondary-100))')

    // Volunteerly uses the primary color for the tabs menu (the default)
    // Mas4you and Roermond use the secondary color for the tabs menu
    const useSecondaryColorForTabsMenu = tenant.value == 'mas4you'
    // Stageplein uses muted color for the tabs menu
    const useMutedColorForTabsMenu = useAppConfig().app?.isStagedoos || tenant.value == 'public'

    if (useSecondaryColorForTabsMenu) {
      // Basically Mas4you theme.
      // Menu border color
      setCssVariable('color-theme-header-border', 'rgb(var(--color-secondary-100))')
      // Menu tabs border color
      setCssVariable('color-theme-menu-tabs-border', 'rgb(var(--color-secondary-200))')
      // Tabs background color, slightly darker than the main background
      setCssVariable('color-theme-menu-tabs-background', 'rgb(var(--color-secondary-100)/.5)')
    }
    else if (useMutedColorForTabsMenu) {
      // Stageplein theme
      // Menu border color
      setCssVariable('color-theme-header-border', 'rgb(var(--color-muted-300))')
      // Menu tabs border color
      setCssVariable('color-theme-menu-tabs-border', 'rgb(var(--color-muted-300))')
      // Tabs background color, slightly darker than the main background
      setCssVariable('color-theme-menu-tabs-background', 'rgb(var(--color-muted-100)/.5)')
    }
    else {
      // Volunteerly theme
      // Menu border color
      setCssVariable('color-theme-header-border', 'rgb(var(--color-primary-200)/.8)')
      // Menu tabs border color
      setCssVariable('color-theme-menu-tabs-border', 'rgb(var(--color-primary-200))')
      // Menu tabs background color, slightly darker than the main background
      setCssVariable('color-theme-menu-tabs-background', 'rgb(var(--color-primary-100)/.5)')
    }

    // Set the default text color for content blocks
    setCssVariable('color-theme-content-text', colorConfig?.textColor?.value || 'rgb(var(--color-primary-950))')
  }

  watch([themeConfig], () => {
    if (import.meta.dev) console.log('[useTheme] 👀 themeConfig changed -> applying now...')
    updateColors(colorConfig)
    applyThemeConfig(themeConfig.value)
  })

  function initTheme() {
    updateColors(colorConfig)
    applyThemeConfig(themeConfig.value)
  }

  const backgroundColorClass = 'bg-theme-background dark:bg-theme-background-dark'

  return {
    backgroundColorClass,
    applyThemeConfig,
    initTheme,
  }
}
